import type { ViewResolution } from '@ayumia/contracts';
import { text, type LocalizedText } from '../app/i18n';

export type ObjectSummary = NonNullable<ViewResolution['object']>;
export type FieldSummary = ObjectSummary['fields'][number];

/** Masqué par la politique (ADR-044) : la clé existe, la valeur non. */
export const MASKED = Symbol('masked');

export function fieldOf(summary: ObjectSummary | undefined, fieldId: string): FieldSummary | undefined {
  return summary?.fields.find((field) => field.field_id === fieldId);
}

export function fieldLabel(summary: ObjectSummary | undefined, fieldId: string, override?: LocalizedText): string {
  if (override !== undefined) return text(override, 'fr', fieldId);
  return text(fieldOf(summary, fieldId)?.label, 'fr', fieldId);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Affichage d'une valeur selon son type du modèle. Aucun arrondi n'est stocké : seul l'affichage
 * formate (H11 : « arrondi à l'affichage, jamais dans la valeur stockée »).
 */
const DECIMAL_TEXT = /^-?\d+(\.\d+)?$/;

/**
 * Formate un décimal sans le convertir en `number` (R2) : Intl.NumberFormat lit une chaîne décimale
 * exactement (ECMA-402, NumberFormat v3), au centime près même au-delà de 2^53. Le type de la
 * bibliothèque ES2022 ne déclare que `number | bigint` : la chaîne passe telle quelle.
 */
export function formatDecimal(value: string | number, locale = 'fr', options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(locale, options).format(value as unknown as number);
}

/** Unité affichée : le symbole que le résumé du type lit au catalogue (« °C » pour celsius, ADR-129), sinon l'identifiant. */
function unitText(unit: string | undefined, field: FieldSummary | undefined): string {
  if (unit === undefined) return '';
  return unit === field?.unit && field.unit_symbol !== undefined ? field.unit_symbol : unit;
}

export function formatValue(value: unknown, field: FieldSummary | undefined, locale = 'fr'): string {
  if (value === null || value === undefined) return '—';
  const type = field?.type;
  if (type === 'boolean' || typeof value === 'boolean') return value === true ? 'Oui' : 'Non';
  if (type === 'enumeration' && typeof value === 'string') return text(field?.enum_labels?.[value], locale, value);
  if (type === 'quantity' && isRecord(value)) {
    const amount = typeof value['value'] === 'number' ? value['value'].toLocaleString(locale) : String(value['value'] ?? '—');
    return `${amount} ${unitText(typeof value['unit'] === 'string' ? value['unit'] : field?.unit, field)}`.trim();
  }
  // Montant (décimal en texte ou nombre, ADR-086) : formaté à l'affichage seulement, quel que soit le type déclaré (json compris).
  if (isRecord(value) && (typeof value['amount'] === 'number' || (typeof value['amount'] === 'string' && DECIMAL_TEXT.test(value['amount']))) && typeof value['currency'] === 'string' && /^[A-Z]{3}$/.test(value['currency'])) {
    return formatDecimal(value['amount'], locale, { style: 'currency', currency: value['currency'] });
  }
  if (isRecord(value) && type !== 'quantity' && (typeof value['value'] === 'number' || typeof value['value'] === 'string') && typeof value['unit'] === 'string' && Object.keys(value).length <= 3) {
    const amount = typeof value['value'] === 'number' ? value['value'].toLocaleString(locale) : String(value['value']);
    return `${amount} ${value['unit']}`;
  }
  if (type === 'date' && typeof value === 'string') {
    const civil = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (civil !== null) return new Date(Number(civil[1]), Number(civil[2]) - 1, Number(civil[3])).toLocaleDateString(locale);
  }
  if ((type === 'date' || type === 'datetime') && typeof value === 'string') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return type === 'date' ? date.toLocaleDateString(locale) : date.toLocaleString(locale);
  }
  if (typeof value === 'number') return `${value.toLocaleString(locale)}${field?.unit === undefined ? '' : ` ${unitText(field.unit, field)}`}`;
  if (typeof value === 'string') return value;
  if (isRecord(value) && Object.keys(value).every((key) => /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/.test(key))) return text(value as LocalizedText, locale);
  if (Array.isArray(value)) return value.map((entry) => formatValue(entry, undefined, locale)).join(', ');
  return JSON.stringify(value);
}

/** Champs masqués ou retirés par la politique pour cette lecture (`meta.permissions_applied`). */
export interface FieldStates {
  masked: ReadonlySet<string>;
  restricted: ReadonlySet<string>;
}

export function fieldStates(meta: { permissions_applied?: { masked_fields?: string[]; restricted_fields?: string[] } } | undefined): FieldStates {
  return { masked: new Set(meta?.permissions_applied?.masked_fields ?? []), restricted: new Set(meta?.permissions_applied?.restricted_fields ?? []) };
}

/** Nombre, quantité ou montant : aligné à droite, en chiffres tabulaires (ADR-094). */
export function isAmountLike(field: FieldSummary | undefined): boolean {
  if (field === undefined) return false;
  if (field.type === 'number' || field.type === 'decimal' || field.type === 'quantity' || field.type === 'money') return true;
  return field.type === 'json' && /(_amount|_weight|_price|_total|_quantity)$|^(amount|quantity|weight|price|total)$/.test(field.field_id);
}
